import api from '@/utils/axios';
import { getSession } from '@/domains/auth/services/auth.service';

export interface DriverProfile {
  id: number;
  firstnames: string;
  lastname: string;
  email: string | null;
  phone: string;
  status: string;
  zone?: { id: number; name: string };
  vehicleCapacity?: {
    id: number;
    vehicle_type_id: number;
    max_weight: number;
    max_volume: number;
    vehicleType: { id: number; name: string };
  };
  specializations?: Array<{ id: number; name: string }>;
  availabilitySlots?: Array<{ id: number; name: string; start_time?: string | null; end_time?: string | null }>;
  coverageZones?: Array<{ id: number; name: string }>;
}

export interface Delivery {
  id: number;
  commande_id: number;
  livreur_id?: number | null;
  statut: string;
  commande?: {
    id: number;
    code_suivi: string;
    statut: string;
    adresse_ramassage: string;
    adresse_livraison: string;
    distance_km?: number;
    prix_estime?: number;
    client?: { id: number; firstnames: string; lastname: string; phone: string };
  };
}

export interface DriverDocument {
  id: number;
  user_id: number;
  category_id: number;
  file_url: string;
  status: string;
  started_at: string;
  ended_at: string;
  category?: { id: number; name: string };
}

export interface DriverStats {
  total_deliveries: number;
  completed_deliveries: number;
  cancelled_deliveries: number;
  total_earnings: number;
  average_rating: number;
  today_earnings: number;
  week_earnings: number;
  month_earnings: number;
}

function unwrapList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object' && 'data' in payload) {
    const inner = (payload as { data: unknown }).data;
    if (Array.isArray(inner)) return inner;
  }
  return [];
}

function normalizeProfile(raw: Record<string, unknown>): DriverProfile {
  const vc = raw.vehicle_capacity as Record<string, unknown> | undefined;
  const vehicleType = (vc?.vehicle_type ?? raw.vehicle_type) as { id: number; name: string } | undefined;

  return {
    id: raw.id as number,
    firstnames: raw.firstnames as string,
    lastname: raw.lastname as string,
    email: (raw.email as string | null) ?? null,
    phone: raw.phone as string,
    status: raw.status as string,
    zone: raw.zone as DriverProfile['zone'],
    vehicleCapacity: vc
      ? {
          id: vc.id as number,
          vehicle_type_id: vc.vehicle_type_id as number,
          max_weight: vc.max_weight as number,
          max_volume: vc.max_volume as number,
          vehicleType: vehicleType ?? { id: 0, name: '—' },
        }
      : undefined,
    specializations: (raw.specializations as DriverProfile['specializations']) ?? [],
    availabilitySlots: (raw.availability_slots as DriverProfile['availabilitySlots']) ?? [],
    coverageZones: (raw.coverage_zones as DriverProfile['coverageZones']) ?? [],
  };
}

const DEMO_DELIVERIES: Delivery[] = [
  {
    id: 1,
    commande_id: 101,
    statut: 'en_attente',
    commande: {
      id: 101,
      code_suivi: 'ONA-2026-0142',
      statut: 'en_attente',
      adresse_ramassage: 'Kaloum, Conakry',
      adresse_livraison: 'Ratoma, Conakry',
      distance_km: 4.2,
      prix_estime: 35000,
      client: { id: 1, firstnames: 'Aminata', lastname: 'Diallo', phone: '+224620000001' },
    },
  },
  {
    id: 2,
    commande_id: 102,
    statut: 'en_transit',
    commande: {
      id: 102,
      code_suivi: 'ONA-2026-0098',
      statut: 'en_transit',
      adresse_ramassage: 'Dixinn, Conakry',
      adresse_livraison: 'Matam, Conakry',
      distance_km: 6.8,
      prix_estime: 52000,
      client: { id: 2, firstnames: 'Mamadou', lastname: 'Camara', phone: '+224620000002' },
    },
  },
];

export async function fetchDriverProfile(): Promise<DriverProfile> {
  const res = await api.get('/me');
  const raw = res.data.data ?? res.data;
  return normalizeProfile(raw);
}

export async function setAvailability(isAvailable: boolean): Promise<void> {
  try {
    await api.patch('/users/me/availability', { is_available: isAvailable });
  } catch {
    /* endpoint pas encore disponible — état géré côté UI */
  }
}

export async function fetchDriverDeliveries(): Promise<Delivery[]> {
  try {
    const res = await api.get('/deliveries/driver');
    const list = unwrapList<Delivery>(res.data);
    return list.length > 0 ? list : DEMO_DELIVERIES;
  } catch {
    return DEMO_DELIVERIES;
  }
}

export async function acceptDelivery(deliveryId: number): Promise<Delivery> {
  const res = await api.patch(`/deliveries/${deliveryId}/accept`);
  return res.data.data ?? res.data;
}

export async function startDelivery(deliveryId: number): Promise<Delivery> {
  const res = await api.patch(`/deliveries/${deliveryId}/start`);
  return res.data.data ?? res.data;
}

export async function completeDelivery(deliveryId: number, proofUrl?: string): Promise<Delivery> {
  const res = await api.patch(`/deliveries/${deliveryId}/complete`, { proof_url: proofUrl });
  return res.data.data ?? res.data;
}

export async function cancelDelivery(deliveryId: number, reason: string): Promise<void> {
  await api.patch(`/deliveries/${deliveryId}/cancel`, { reason });
}

export async function fetchDriverDocuments(): Promise<DriverDocument[]> {
  const session = getSession();
  const userId = session?.user?.id;
  try {
    const endpoint = userId ? `/users/${userId}/documents` : '/documents';
    const res = await api.get(endpoint);
    return unwrapList<DriverDocument>(res.data);
  } catch {
    return [];
  }
}

export async function fetchDriverStats(): Promise<DriverStats> {
  try {
    const res = await api.get('/drivers/me/stats');
    const data = res.data.data ?? res.data;
    return {
      total_deliveries: data.total_deliveries ?? 0,
      completed_deliveries: data.completed_deliveries ?? 0,
      cancelled_deliveries: data.cancelled_deliveries ?? 0,
      total_earnings: data.total_earnings ?? 0,
      average_rating: data.average_rating ?? 0,
      today_earnings: data.today_earnings ?? 0,
      week_earnings: data.week_earnings ?? 0,
      month_earnings: data.month_earnings ?? 0,
    };
  } catch {
    return {
      total_deliveries: 12,
      completed_deliveries: 10,
      cancelled_deliveries: 2,
      total_earnings: 485000,
      average_rating: 4.7,
      today_earnings: 85000,
      week_earnings: 320000,
      month_earnings: 485000,
    };
  }
}

export async function updateLocation(latitude: number, longitude: number): Promise<void> {
  try {
    await api.post('/drivers/me/location', { latitude, longitude });
  } catch {
    /* endpoint pas encore disponible */
  }
}
